import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Briefcase, Plus, Trash2 } from "lucide-react";

interface AddJobDialogProps {
  jobs: string[];
  onAddJob: (job: string) => void;
  onDeleteJob: (job: string) => void;
  customerCountByJob: Record<string, number>;
}

export function AddJobDialog({ jobs, onAddJob, onDeleteJob, customerCountByJob }: AddJobDialogProps) {
  const [open, setOpen] = useState(false);
  const [newJob, setNewJob] = useState("");

  const handleAddJob = () => {
    if (newJob.trim() && !jobs.includes(newJob.trim())) {
      onAddJob(newJob.trim());
      setNewJob("");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddJob();
    }
  };

  const handleDeleteJob = (job: string) => {
    const count = customerCountByJob[job] || 0;
    if (count > 0) {
      alert(`ناتوانرێت ئەم پیشەیە بسڕێتەوە چونکە ${count} کڕیار لەسەری هەیە`);
      return;
    }
    if (confirm(`دڵنیایت لە سڕینەوەی پیشەی "${job}"؟`)) {
      onDeleteJob(job);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="flex items-center gap-2 text-xs md:text-sm px-3 py-2 md:px-4 md:py-2">
          <Briefcase className="h-3 w-3 md:h-4 md:w-4" />
          <span className="hidden sm:inline">زیادکردنی پیشە</span>
          <span className="sm:hidden">پیشە</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg h-[500px] flex flex-col" dir="rtl">
        <DialogHeader className="text-right">
          <DialogTitle className="text-right">بەڕێوەبردنی پیشەکان</DialogTitle>
          <DialogDescription className="text-right">{jobs.length} پیشە تۆمارکراوە</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col flex-1 overflow-hidden space-y-4">
          <div className="flex gap-2">
            <Input
              value={newJob}
              onChange={(e) => setNewJob(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="ناوی پیشەی نوێ"
              className="flex-1"
            />
            <Button onClick={handleAddJob} size="icon">
              <Plus className="h-4 w-4" />
            </Button>
          </div>
          <div className="border rounded-md flex-1 overflow-y-auto">
            {jobs.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                هیچ پیشەیەک نییە
              </div>
            ) : (
              <div className="divide-y">
                {jobs.map((job) => {
                  const count = customerCountByJob[job] || 0;
                  return (
                    <div key={job} className="flex items-center justify-between p-3">
                      <div className="flex items-center gap-2">
                        <span>{job}</span>
                        <Badge variant="secondary">{count} کڕیار</Badge>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteJob(job)}
                        disabled={count > 0}
                        className="h-8 w-8 text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
